import { ClerkProvider } from '@clerk/clerk-react';
import { enUS, frFR } from '@clerk/localizations';
import { dark } from '@clerk/themes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import { AntdThemeProvider } from '@/app/providers/AntdThemeProvider';
import { ThemeProvider, useTheme } from '@/app/providers/ThemeProvider';
import { AppRouter } from '@/app/router';
import { ModalStackProvider } from '@/shared/contexts/ModalStackContext';
import { useUserLanguageSetup } from '@/shared/hooks/useUserLanguage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
    },
  },
});

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!publishableKey) {
  throw new Error('Missing Clerk Publishable Key');
}

// Rendu sous ThemeProvider pour que les écrans Clerk (sign-in, account…)
// suivent le mode clair/sombre et la langue active.
const LocalizedClerkProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { theme } = useTheme();
  const { i18n } = useTranslation();

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      localization={i18n.language?.startsWith('en') ? enUS : frFR}
      appearance={{
        baseTheme: theme === 'dark' ? dark : undefined,
      }}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignOutUrl={import.meta.env.VITE_AFTER_SIGNOUT_URL || '/'}
    >
      {children}
    </ClerkProvider>
  );
};

function AppContent() {
  // Setup de langue au chargement d'une session authentifiée (détection
  // navigateur si aucun choix explicite mémorisé) — nécessite le contexte
  // du router (useAuth y navigue) et le QueryClient, tous deux disponibles ici.
  useUserLanguageSetup();

  return (
    <>
      <AppRouter />
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <LocalizedClerkProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AntdThemeProvider>
              <ModalStackProvider>
                <AppContent />
              </ModalStackProvider>
            </AntdThemeProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </LocalizedClerkProvider>
    </ThemeProvider>
  );
}
